import { PlusOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import React, { useState } from 'react';

import { SortableCollapse } from 'app/components/layout/Sortable/Collapse';
import type { Picture } from 'app/typings';
import type { GuideSection } from 'app/typings/guides';

import { SectionPackageComponent } from './SectionPackage/SectionPackage';
import './SectionPackages.scss';

type Field = {
  name: number;
  key: number;
};

type Props = {
  fields: Field[];
  add: (defaultValue?: unknown, insertIndex?: number) => void;
  remove: (index: number) => void;
  move: (from: number, to: number) => void;
  guideSection?: Partial<GuideSection>;
  formattedPackages: {
    id: number;
    name: string;
    coverPicture: Picture;
    searchName?: string;
  }[];
};

export const SectionPackages = ({
  fields,
  add,
  remove,
  move,
  guideSection,
  formattedPackages,
}: Props) => {
  const [activeKeys, setActiveKeys] = useState(['pkg-0']);

  const handleAdd = () => {
    setActiveKeys((prevActiveKeys) => [
      ...prevActiveKeys,
      `pkg-${fields.length}`,
    ]);
    add({});
  };

  const handleRemove = (index: number) => {
    remove(index);

    const sectionPackage = guideSection?.packages?.[index];

    if (sectionPackage?.id) {
      add({ ...sectionPackage, id: -sectionPackage.id }, index);
    }
  };

  const visibleFields = fields.filter(({ name }) => {
    const sectionPackage = guideSection?.packages?.[name];

    return !(sectionPackage?.id !== undefined && sectionPackage?.id < 0);
  });

  const itemIds = visibleFields.map(({ key }) => `pkg-${key}`);

  const handleSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    const from = visibleFields[oldIndex]?.name;
    const to = visibleFields[newIndex]?.name;

    if (from !== undefined && to !== undefined) {
      move(from, to);
    }
  };

  return (
    <>
      <SortableCollapse
        activeKey={activeKeys}
        onToggle={setActiveKeys}
        items={itemIds}
        onSortEnd={handleSortEnd}
      >
        {fields.map((field, index) => {
          const { name, key } = field;
          const sectionPackage = guideSection?.packages?.[name];

          if (sectionPackage?.id !== undefined && sectionPackage?.id < 0) {
            return null;
          }

          return (
            <SectionPackageComponent
              key={`pkg-${key}`}
              handleRemove={handleRemove}
              field={field}
              index={index}
              sectionPackage={sectionPackage}
              formattedPackages={formattedPackages}
              id={`pkg-${key}`}
            />
          );
        })}
      </SortableCollapse>
      <div className="section-packages__add">
        <Button icon={<PlusOutlined />} onClick={handleAdd}>
          Add Package
        </Button>
      </div>
    </>
  );
};
